/**
 * What the customer actually paid on an invoice, as opposed to what the invoice
 * was credited.
 *
 * A card price difference (surcharge) is charged ON TOP of the money applied to
 * the invoice: a $250 invoice paid by credit card credits `amountPaid` $250
 * while $256.50 leaves the card. The invoice's own totals must keep footing
 * (Total − Amount Paid = Balance Due), so the fee never goes into those rows —
 * but a page that prints only them tells the customer they paid $250 when their
 * statement and their receipt both say $256.50.
 *
 * This is the one derivation every customer invoice page uses to state the
 * charged figure, so the hosted page, the portal and the payment banner cannot
 * each add it up differently. The receipt (`receipts.service.ts`) already
 * reports `amount + surchargeAmount`; this matches it.
 *
 * The fee figures are the server's (`depositServiceFeeAmount` /
 * `paymentServiceFeeAmount`), read off the payments the fee was charged on —
 * never recomputed from the tenant's current rate, and zero where a fee was
 * reversed off a debit card.
 */
export interface InvoicePaidFigures {
  amountPaid?: number | null;
  /** Paid gratuity; not part of `amountPaid`, but it rode the same card. */
  tipAmount?: number | null;
  depositApplied?: number | null;
  depositServiceFeeAmount?: number | null;
  paymentServiceFeeAmount?: number | null;
}

export interface InvoiceTotalPaid {
  /** Card price difference charged on top of the money applied to the invoice. */
  cardPriceDifference: number;
  /** The paid tip folded into `totalPaid`, so a caller can name it. */
  tip: number;
  /** Everything the customer paid: invoice credit + tip + card price difference. */
  totalPaid: number;
}

// Coerced, not type-checked: a money figure that reaches the browser as a
// decimal string must still count rather than silently reading as zero.
const nonNegative = (value: number | null | undefined): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const cents = (value: number): number => Math.round(value * 100) / 100;

/**
 * Null when there is nothing beyond "Amount Paid" to say: no money was applied,
 * or the card was charged exactly what the invoice was credited.
 *
 * A tip counts as much as a fee. It is paid money that never reaches
 * `amountPaid`, so a $100 invoice settled with a $20 tip charged the card $120
 * — and gating this on the fee alone is what let that page go on saying "a
 * total of $100.00 has been applied".
 */
export function invoiceTotalPaid(figures: InvoicePaidFigures): InvoiceTotalPaid | null {
  const amountPaid = nonNegative(figures.amountPaid);
  if (amountPaid === 0) return null;
  // Same gate the invoice documents apply (`invoiceDocMoney`): a deposit fee is
  // only this invoice's money while a deposit is actually applied to it.
  const depositFee =
    nonNegative(figures.depositApplied) > 0 ? nonNegative(figures.depositServiceFeeAmount) : 0;
  const cardPriceDifference = cents(depositFee + nonNegative(figures.paymentServiceFeeAmount));
  const tip = nonNegative(figures.tipAmount);
  if (cardPriceDifference === 0 && tip === 0) return null;
  return {
    cardPriceDifference,
    tip,
    totalPaid: cents(amountPaid + tip + cardPriceDifference),
  };
}

/**
 * What was actually charged, as a sentence — or null when the card was charged
 * exactly what the invoice was credited and the invoice rows already say it.
 *
 * This is the line any paid invoice surface can add next to its own thank-you,
 * which is why it does not repeat "paid in full": the Stripe-return banner
 * below is not the only place a payer lands. A Payabli payer never gets that
 * redirect, so gating this sentence on it left the entire Payabli tenant base
 * reading "a total of $250.00 has been applied" after a $294.00 charge.
 */
export function invoiceChargedTotalNote(
  figures: InvoicePaidFigures,
  fmt: (amount: number) => string,
): string | null {
  const paid = invoiceTotalPaid(figures);
  if (!paid) return null;
  const parts = [
    ...(paid.tip > 0 ? [`a ${fmt(paid.tip)} tip`] : []),
    ...(paid.cardPriceDifference > 0
      ? [`a ${fmt(paid.cardPriceDifference)} card price difference`]
      : []),
  ];
  return `A total of ${fmt(paid.totalPaid)} was paid, including ${parts.join(' and ')}.`;
}

/**
 * The paid-in-full confirmation sentence. When the card was charged exactly the
 * invoice credit it is that figure, as before; otherwise it states the charged
 * total and names what is in it, so it agrees with the receipt.
 */
export function invoicePaidInFullMessage(
  figures: InvoicePaidFigures,
  fmt: (amount: number) => string,
): string {
  const note = invoiceChargedTotalNote(figures, fmt);
  if (!note) {
    return `This invoice is paid in full. A total of ${fmt(
      nonNegative(figures.amountPaid),
    )} has been applied.`;
  }
  return `This invoice is paid in full. ${note}`;
}
